import React, { useRef, useCallback } from 'react';
import { Paperclip, X, FileText, Image } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import { validateFile } from '../../../../utils/attachmentHelpers';

/**
 * EmailConfigContent — Email step configuration form.
 *
 * Features:
 *  - Variable insertion buttons
 *  - Subject line (plain text input)
 *  - Rich-text body editor (bold, italic, links)
 *  - File attachments (multiple, validated, removable)
 *
 * Props:
 *  - subject, body, attachments, onChange
 *  - subjectRef, bodyRef, onFocusField, onInsertVariable
 */
const EmailConfigContent = ({
    subject = '',
    body = '',
    attachments = [],
    onChange,
    subjectRef,
    bodyRef,
    onFocusField,
    onInsertVariable,
}) => {
    const fileInputRef = useRef(null);

    // Handle body HTML change from RichTextEditor
    const handleBodyChange = useCallback((html) => {
        onChange && onChange({ body: html });
    }, [onChange]);

    // Insert a variable token into the rich-text editor
    const handleInsertVar = useCallback((token) => {
        if (onInsertVariable) {
            onInsertVariable(token);
        }
    }, [onInsertVariable]);

    // ========== ATTACHMENT HANDLERS ==========
    const handleFileSelect = useCallback((e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        const newAttachments = [];
        for (const file of files) {
            const validation = validateFile(file);
            if (!validation.valid) {
                console.warn(`File rejected: ${file.name} — ${validation.reason}`);
                continue;
            }
            newAttachments.push({
                id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                file, // raw File object — will be uploaded on automation save/activate
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type || 'application/octet-stream',
                status: 'pending', // not yet uploaded to Storage
            });
        }

        if (newAttachments.length) {
            onChange && onChange({
                attachments: [...(attachments || []), ...newAttachments],
            });
        }

        // Reset file input so the same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [attachments, onChange]);

    const handleRemoveAttachment = useCallback((idToRemove) => {
        onChange && onChange({
            attachments: (attachments || []).filter(a => (a.id || a.attachmentId) !== idToRemove),
        });
    }, [attachments, onChange]);

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const getFileIcon = (mimeType) => {
        if (mimeType && mimeType.startsWith('image/')) return Image;
        return FileText;
    };

    const currentAttachments = attachments || [];

    return (
        <>
            {/* Variables */}
            <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-3">Variables (Click to Insert)</label>
                <div className="flex flex-wrap gap-2">
                    {['{{MyName}}', '{{FirstName}}', '{{LastName}}', '{{FullName}}', '{{InterestedProperty}}', '{{MyPhone}}'].map(token => (
                        <button
                            key={token}
                            onClick={() => handleInsertVar(token)}
                            className="px-2 py-1.5 bg-stone-100 rounded text-[10px] text-stone-600 border border-stone-200"
                        >
                            {token}
                        </button>
                    ))}
                </div>
            </div>

            {/* Subject */}
            <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Subject Line</label>
                <input
                    ref={subjectRef}
                    onFocus={() => onFocusField && onFocusField('subject')}
                    type="text"
                    value={subject}
                    onChange={(e) => onChange && onChange({ subject: e.target.value })}
                    placeholder="Quick question about {{Interest Address}}"
                    className="w-full bg-white border border-notion-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-stone-400"
                />
            </div>

            {/* Body — Rich Text Editor */}
            <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Body</label>
                <RichTextEditor
                    value={body}
                    onChange={handleBodyChange}
                    editorRef={bodyRef}
                    onFocus={() => onFocusField && onFocusField('body')}
                    placeholder={`Hi {{First Name}},\n\nThanks for visiting {{Interest Address}} today.\n\nLet me know if you want a private tour?\n\nBest,\n{{Agent Name}}`}
                />
            </div>

            {/* Attachments */}
            <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Attachments</label>

                {/* Attached files list */}
                {currentAttachments.length > 0 && (
                    <div className="space-y-2 mb-3">
                        {currentAttachments.map((att) => {
                            const id = att.id || att.attachmentId;
                            const name = att.fileName || att.filename || 'Untitled';
                            const size = att.fileSize || att.size || 0;
                            const mime = att.fileType || att.mimeType || '';
                            const Icon = getFileIcon(mime);
                            return (
                                <div key={id} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2">
                                    <Icon className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs text-stone-700 truncate">{name}</p>
                                        <p className="text-[10px] text-stone-400">{formatFileSize(size)}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveAttachment(id)}
                                        className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-600 flex-shrink-0"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Add files button */}
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 bg-white border border-dashed border-stone-300 rounded-lg text-xs text-stone-500 hover:border-stone-400 hover:text-stone-600 transition-colors w-full justify-center"
                >
                    <Paperclip className="w-3.5 h-3.5" />
                    Attach Files
                </button>
                <p className="text-[10px] text-stone-400 mt-1.5">PDF, images, Word docs, text files. Max 10 MB each.</p>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.txt"
                    onChange={handleFileSelect}
                    className="hidden"
                />
            </div>
        </>
    );
};

export default EmailConfigContent;
